import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";

interface ProhibitedCategory {
  id: string;
  name: string;
  description: string | null;
  keywords: string[] | null;
  severity: string;
}

interface ProhibitedCategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category?: ProhibitedCategory | null;
}

export function ProhibitedCategoryDialog({ open, onOpenChange, category }: ProhibitedCategoryDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const formData = new FormData(e.currentTarget);
      const keywordsString = formData.get("keywords") as string;
      const keywords = keywordsString.split(",").map(k => k.trim()).filter(k => k.length > 0);

      const categoryData = {
        name: formData.get("name") as string,
        description: formData.get("description") as string,
        keywords,
        severity: formData.get("severity") as string,
      };

      if (category) {
        // Update existing category
        const { error } = await supabase
          .from("prohibited_categories" as any)
          .update(categoryData as any)
          .eq("id", category.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Category updated successfully",
        });
      } else {
        // Create new category
        const { error } = await supabase
          .from("prohibited_categories" as any)
          .insert([categoryData] as any);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Category created successfully",
        });
      }

      queryClient.invalidateQueries({ queryKey: ["prohibited-categories"] });
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {category ? "Edit Category" : "Add New Category"}
          </DialogTitle>
          <DialogDescription>
            {category 
              ? "Update the prohibited category details below" 
              : "Create a new prohibited category for compliance monitoring"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Category Name</Label>
              <Input
                id="name"
                name="name"
                defaultValue={category?.name || ""}
                placeholder="e.g., Gambling & Sports Betting"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                name="description"
                defaultValue={category?.description || ""}
                placeholder="Describe what this category includes..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="keywords">Keywords (comma-separated)</Label>
              <Input
                id="keywords"
                name="keywords"
                defaultValue={category?.keywords?.join(", ") || ""}
                placeholder="gambling, betting, casino, poker"
              />
              <p className="text-xs text-muted-foreground">
                Deals containing these keywords will be flagged
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="severity">Severity</Label>
              <Select name="severity" defaultValue={category?.severity || "warning"}>
                <SelectTrigger>
                  <SelectValue placeholder="Select severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="blocking">Blocking</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Blocking categories prevent deal submission, warnings require review
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                category ? "Update Category" : "Create Category"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
